import {DrawerFooter} from "@/widget/DrawerModal/DrawerModal";
import TextField from "@mui/material/TextField";
import {Controller, useForm} from "react-hook-form";
import {Autocomplete, Grid, Box} from "@mui/material";
import FormControl from "@mui/material/FormControl";
import React, {FC} from "react"
import {useDialogModal, useGlobalActions} from "@/store/global.c";
import * as z from "zod";
import {zodResolver} from "@hookform/resolvers/zod";
import {emptyValue, setValues} from "@/helper/method";
import {InputFile} from "@/widget/UploadFile/InputFile";
import {UploadFileVO} from "@/service/common.s";
import {useSaveFile} from "@/service/file.s";
import {DictPO} from "@/model/system";

const validationSchema = z.object({
    url: z.string({message: "请上传文件"}),
    name: z.string({message: "请填写文件名称"}),
    remark: z.unknown(),
    tag: z.number().default(0),
    file: z.any(),
    size: z.unknown(),
    type: z.unknown(),
    category: z.string({message: "请选择文件类型"}),
    categoryId: z.unknown(),
    extra: z.object({category: z.unknown(), categoryId: z.unknown()}).nullish(),
    targetId: z.number().optional().default(0),
})

export const uploadFileSchema = z.object({
    url: z.string({message: "请上传文件"}).nullable(),
    name: z.string({message: "请填写文件名称"}).nullable(),
    size: z.unknown(),
    type: z.unknown(),
})

export type WriteFileForm = z.infer<typeof validationSchema>

const defaultValues = emptyValue(validationSchema)

export type WriteFileType = {
    tag: number
    onSearch: (params: any) => void
    title: string
    options: DictPO[]
}

interface WriteFileProps {
    tag: number // 1.合同附件 2.专利附件 3.商标附件 4.待办附件 7.头像 9.总结 11.合同原件 12.报销附件 15.交易产品缩略图 16.服务产品缩略图 17.交易产品详情图 18.资讯封面图 19.定稿文件 20.官方来文
    targetId: number
    options: DictPO[]
}

export const WriteFile: FC<WriteFileProps> = ({options, tag, targetId}) => {
    const {setToast} = useGlobalActions()
    const {mutateAsync: saveFileMutate, isPending: saveFilePending} = useSaveFile()
    const {setDialogModal,} = useGlobalActions()
    const {id: dialogId, onSearch} = useDialogModal()
    const {
        register,
        control,
        handleSubmit,
        setValue,
        reset,
        formState: {errors},
    } = useForm<WriteFileForm>({
        resolver: zodResolver(validationSchema),
        values: {tag} as WriteFileForm,
        defaultValues,
    });

    const onSubmit = async (values: WriteFileForm) => {
        const result = await saveFileMutate({
            ...values, tag, extra: {
                category: values?.category,
                categoryId: values?.categoryId?.toString(),
            }, targetId
        })
        if (result.code == 0) {
            setDialogModal({open: false})
            onSearch && onSearch()
        } else {
            setToast({open: true, message: result.msg, severity: "error", variant: 'standard'})
        }
    }

    return (
        <Box component={'form'} noValidate onSubmit={handleSubmit(onSubmit)}>
            <Grid container spacing={3}>
                <Grid size={12}>
                    <InputFile<WriteFileForm> maxTagWidth={'200px'} tag={tag} isSave={false} uploadCallback={(file: UploadFileVO) => {
                        setValues<UploadFileVO>(file, setValue)
                    }} mode={'single'} targetId={dialogId} attr={register("file")} control={control}
                                              errMsg={errors?.url?.message}/>
                </Grid>
                <Grid size={6}>
                    <TextField error={!!errors?.name}
                               helperText={errors?.name?.message}
                               variant="outlined"
                               {...register("name")}
                               placeholder={"填写文件名称"}/>
                </Grid>
                <Grid size={6}>
                    <FormControl fullWidth={true} required={true}>
                        <Controller control={control} render={({field: {onChange}}) => <Autocomplete
                            options={options || []}
                            fullWidth
                            onChange={(event, option) => {
                                setValue("categoryId", option?.value)
                                onChange(option?.label)

                            }}
                            renderInput={(params) => (
                                <TextField error={!!errors?.category}
                                           helperText={errors?.category?.message}
                                           variant="outlined"
                                           placeholder={"选择文件类型"} {...params} />
                            )}
                        />} name={"category"}/>
                    </FormControl>
                </Grid>

                <Grid size={12}>
                    <FormControl fullWidth={true}>
                        <TextField
                            multiline
                            rows={3}
                            {...register("remark")}
                            slotProps={{
                                input: {
                                    sx: {height: 'auto', minHeight: 80},
                                },
                            }}
                            placeholder="填写备注"
                            fullWidth
                        />
                    </FormControl>
                </Grid>
            </Grid>
            <DrawerFooter loading={saveFilePending} onReset={() => reset(defaultValues)}/>
        </Box>
    );
};
